import ReactECharts from 'echarts-for-react';
import stackData from '../data/stackDependence.json';
import { useIsNarrow } from '../../hooks/useIsNarrow';

/**
 * RemedyLagChart — months from complaint to final resolution across the four
 * modern US tech-monopoly cases (Fig 1). Horizontal bar, because the category
 * labels are long and the comparison is "how long", not "when". Brand orange
 * marks the only case in which structure actually changed.
 *
 * Months are DERIVED from the ISO dates in stackDependence.json, never typed
 * in, so a date correction moves the bar (Rule 27).
 */

const BRAND = {
  orange: '#FD9800',
  muted: '#9CA3AF',
  mutedDark: '#6B7280',
  surface: '#171717',
};

type LagItem = {
  case: string;
  filed: string;
  resolved: string;
  structureChanged: boolean;
  final: boolean;
  detail: string;
};

const monthsBetween = (from: string, to: string) => {
  const a = new Date(`${from}T00:00:00Z`);
  const b = new Date(`${to}T00:00:00Z`);
  return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24 * 30.4375));
};

const fmt = (months: number) => {
  const years = Math.floor(months / 12);
  const rest = months % 12;
  return rest === 0 ? `${years}y` : `${years}y ${rest}m`;
};

export const RemedyLagChart = ({ height = 340 }: { height?: number }) => {
  const narrow = useIsNarrow();
  const fig = (
    stackData as unknown as {
      figures: {
        remedy_lag: { source: string; note: string; unit: string; items: LagItem[] };
      };
    }
  ).figures.remedy_lag;

  const rows = fig.items.map((i) => ({ ...i, months: monthsBetween(i.filed, i.resolved) }));
  const maxMonths = Math.max(...rows.map((r) => r.months));

  const option = {
    backgroundColor: BRAND.surface,
    // Deterministic paint: a chart that animates from zero renders as empty bars
    // in a throttled/background tab, which is how readers open a post from a feed.
    animation: false,
    tooltip: {
      trigger: 'item',
      backgroundColor: '#0a0a0a',
      borderColor: '#333',
      textStyle: { color: '#f5f5f5', fontSize: 13 },
      formatter: (p: { dataIndex: number }) => {
        const r = rows[p.dataIndex];
        return [
          `<b>${r.case}</b>`,
          `${fmt(r.months)} from complaint (${r.filed}) to ${r.final ? 'final resolution' : 'latest ruling'} (${r.resolved})`,
          `Structure changed: <b>${r.structureChanged ? 'yes' : 'no'}</b>${r.final ? '' : ' — on appeal'}`,
          `<span style="color:${BRAND.muted}">${r.detail}</span>`,
        ].join('<br/>');
      },
    },
    grid: { left: narrow ? 86 : 112, right: narrow ? 48 : 62, top: narrow ? 12 : 16, bottom: narrow ? 26 : 30 },
    xAxis: {
      type: 'value',
      max: Math.ceil(maxMonths / 24) * 24,
      interval: 24,
      axisLabel: {
        color: BRAND.muted,
        fontSize: narrow ? 11 : 12,
        formatter: (v: number) => (v === 0 ? '0' : `${v / 12}y`),
      },
      splitLine: { lineStyle: { color: '#333', opacity: 0.5 } },
    },
    yAxis: {
      type: 'category',
      data: rows.map((r) => r.case),
      inverse: true,
      axisLine: { lineStyle: { color: '#333' } },
      axisTick: { show: false },
      axisLabel: { color: '#e5e5e5', fontSize: narrow ? 11 : 13, fontWeight: 700 },
    },
    series: [
      {
        type: 'bar',
        barWidth: '52%',
        data: rows.map((r) => ({
          value: r.months,
          itemStyle: {
            color: r.structureChanged ? BRAND.orange : r.final ? BRAND.muted : BRAND.mutedDark,
            borderRadius: [0, 4, 4, 0],
          },
        })),
        label: {
          show: true,
          position: 'right',
          color: '#e5e5e5',
          fontWeight: 700,
          fontSize: narrow ? 11 : 12,
          formatter: (p: { dataIndex: number }) =>
            `${fmt(rows[p.dataIndex].months)}${rows[p.dataIndex].final ? '' : ' *'}`,
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-[#333] bg-[#171717] p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-gray-400 text-center px-4">
        Figure 3. Months from complaint to final resolution in the four cases. Orange is the only case in
        which corporate structure changed. * Ends at the latest ruling, not at a final outcome: both are on
        appeal. {fig.note}
      </p>
    </div>
  );
};
